import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { ConstructorEventUpdateSQLStatus } from "../../../Event/event";

const FractionDecimalFunland = () => {
  const [mode, setMode] = useState("fraction");
  const [step, setStep] = useState("landing");
  const [currentFractionQ, setCurrentFractionQ] = useState(null);
  const [currentDecimalQ, setCurrentDecimalQ] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fractionAnswer, setFractionAnswer] = useState("");
  const [decimalAnswer, setDecimalAnswer] = useState("");
  const [fractionFeedback, setFractionFeedback] = useState("");
  const [decimalFeedback, setDecimalFeedback] = useState("");

  const correctSound = useRef(null);
  const wrongSound = useRef(null);

  const fractionQuestions = [
    { fraction: "1/2", decimal: "0.5" },
    { fraction: "1/4", decimal: "0.25" },
    { fraction: "3/4", decimal: "0.75" },
    { fraction: "1/5", decimal: "0.2" },
    { fraction: "2/5", decimal: "0.4" },
    { fraction: "3/5", decimal: "0.6" },
    { fraction: "4/5", decimal: "0.8" },
    { fraction: "1/10", decimal: "0.1" },
    { fraction: "3/10", decimal: "0.3" },
    { fraction: "7/10", decimal: "0.7" },
    { fraction: "9/10", decimal: "0.9" },
    { fraction: "1/20", decimal: "0.05" },
    { fraction: "2/10", decimal: "0.2" },
    { fraction: "5/10", decimal: "0.5" },
    { fraction: "2/4", decimal: "0.5" },
    { fraction: "4/10", decimal: "0.4" },
  ];

  const startGame = () => {
    if (mode === "fraction") {
      generateFractionQuestion();
      setStep("fraction");
    } else {
      generateDecimalQuestion();
      setStep("decimal");
    }
  };

  const generateFractionQuestion = () => {
    const q = fractionQuestions[Math.floor(Math.random() * fractionQuestions.length)];
    setCurrentFractionQ(q);
    setFractionAnswer("");
    setFractionFeedback("");
  };

  const generateDecimalQuestion = () => {
    const q = fractionQuestions[Math.floor(Math.random() * fractionQuestions.length)];
    setCurrentDecimalQ(q);
    setDecimalAnswer("");
    setDecimalFeedback("");
  };

  const checkFractionAnswer = () => {
    if (fractionAnswer.trim() === currentFractionQ.decimal) {
      ConstructorEventUpdateSQLStatus();
      setFractionFeedback("✅ Correct!");
      correctSound.current.play();
      setTimeout(generateFractionQuestion, 1500);
    } else {
      setFractionFeedback("❌ Try Again!");
      wrongSound.current.play();
    }
  };

  const normalizeFraction = (fracStr) => {
    if (!fracStr.includes("/")) return fracStr;
    const [num, den] = fracStr.split("/").map(Number);
    const gcd = (a, b) => (b ? gcd(b, a % b) : a);
    const g = gcd(num, den);
    return `${num / g}/${den / g}`;
  };

  const checkDecimalAnswer = () => {
    const userAnswer = normalizeFraction(decimalAnswer.trim());
    const correct = normalizeFraction(currentDecimalQ.fraction);
    if (userAnswer === correct) {
      ConstructorEventUpdateSQLStatus();
      setDecimalFeedback("✅ Correct!");
      correctSound.current.play();
      setTimeout(generateDecimalQuestion, 1500);
    } else {
      setDecimalFeedback("❌ Try Again!");
      wrongSound.current.play();
    }
  };

  const nextQuestion = () => {
    setFractionFeedback("");
    setDecimalFeedback("");
    setCurrentIndex((prev) => (prev + 1) % fractionQuestions.length);
    if (mode === "fraction") generateFractionQuestion();
    else generateDecimalQuestion();
  };

  const prevQuestion = () => {
    setFractionFeedback("");
    setDecimalFeedback("");
    setCurrentIndex((prev) => (prev - 1 + fractionQuestions.length) % fractionQuestions.length);
    if (mode === "fraction") generateFractionQuestion();
    else generateDecimalQuestion();
  };

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        background: "radial-gradient(circle at top,rgb(101, 139, 39), #000)",
        color: "#fff",
        fontFamily: "'Orbitron', sans-serif",
        overflow: "hidden",
        textAlign: "center",
      }}
    >
      {/* Animated Gears Background */}
      <div
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          overflow: "hidden",
          zIndex: 0,
        }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
          style={{
            position: "absolute",
            top: "10%",
            left: "5%",
            width: "200px",
            height: "200px",
            border: "6px solid #00e5ff",
            borderRadius: "50%",
            opacity: 0.2,
          }}
        ></motion.div>

        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 90, repeat: Infinity, ease: "linear" }}
          style={{
            position: "absolute",
            bottom: "10%",
            right: "5%",
            width: "250px",
            height: "250px",
            border: "6px solid gold",
            borderRadius: "50%",
            opacity: 0.15,
          }}
        ></motion.div>
      </div>

      <div style={{ position: "relative", zIndex: 1, paddingTop: "5vh" }}>
        {step === "landing" && (
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1 }}
          >
            <h1 style={{ fontSize: "3rem", textShadow: "0 0 20px #00e5ff" }}>
              ⚙️ Fraction & Decimal Funland ⚙️
            </h1>
            <p style={{ color: "#ccc", fontSize: "1.2rem" }}>
              Sharpen your math skills in this futuristic number challenge!
            </p>

            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              style={{
                marginTop: "20px",
                padding: "10px",
                borderRadius: "8px",
                fontSize: "1rem",
                color: "#00e5ff",
                background: "rgba(255,255,255,0.1)",
                border: "1px solid #00e5ff",
                outline: "none",
              }}
            >
              <option value="fraction">Fraction ➜ Decimal</option>
              <option value="decimal">Decimal ➜ Fraction</option>
            </select>

            <br />
            <button
              onClick={startGame}
              style={{
                marginTop: "30px",
                background: "linear-gradient(45deg, #00e5ff, #0078ff)",
                border: "none",
                borderRadius: "12px",
                padding: "15px 40px",
                color: "white",
                fontSize: "1.2rem",
                boxShadow: "0 0 20px #00e5ff",
                cursor: "pointer",
                transition: "0.3s",
              }}
              onMouseOver={(e) => (e.target.style.boxShadow = "0 0 30px gold")}
              onMouseOut={(e) => (e.target.style.boxShadow = "0 0 20px #00e5ff")}
            >
              🚀 Start Game
            </button>
          </motion.div>
        )}

        {step === "fraction" && currentFractionQ && (
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <h2 style={{ fontSize: "2rem", color: "gold" }}>
              Convert <span style={{ color: "#00e5ff" }}>{currentFractionQ.fraction}</span> to Decimal
            </h2>
            <input
              type="text"
              value={fractionAnswer}
              onChange={(e) => setFractionAnswer(e.target.value)}
              placeholder="Enter decimal"
              style={{
                marginTop: "20px",
                padding: "10px 15px",
                borderRadius: "10px",
                border: "2px solid rgb(255, 145, 0)",
                fontSize: "1.2rem",
                textAlign: "center",
                color: "#00e5ff",
                background: "rgba(0,0,0,0.3)",
              }}
            />
            <br />
            <button style={{ marginTop: "20px" }} onClick={checkFractionAnswer}>
              Submit
            </button>
            <div style={{ marginTop: "10px", fontSize: "1.5rem" }}>{fractionFeedback}</div>
            <div style={{ marginTop: "20px" }}>
              <button onClick={prevQuestion}>⬅ Prev</button>
              <button onClick={nextQuestion} style={{ marginLeft: "10px" }}>
                Next ➡
              </button>
            </div>
          </motion.div>
        )}

        {step === "decimal" && currentDecimalQ && (
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <h2 style={{ fontSize: "2rem", color: "gold" }}>
              Convert <span style={{ color: "#00e5ff" }}>{currentDecimalQ.decimal}</span> to Fraction
            </h2>
            <input
              type="text"
              value={decimalAnswer}
              onChange={(e) => setDecimalAnswer(e.target.value)}
              placeholder="Enter fraction (e.g., 1/2)"
              style={{
                marginTop: "20px",
                padding: "10px 15px",
                borderRadius: "10px",
                border: "2px solid rgb(255, 196, 0)",
                fontSize: "1.2rem",
                textAlign: "center",
                color: "#00e5ff",
                background: "rgba(0,0,0,0.3)",
              }}
            />
            <br />
            <button style={{ marginTop: "20px" }} onClick={checkDecimalAnswer}>
              Submit
            </button>
            <div style={{ marginTop: "10px", fontSize: "1.5rem" }}>{decimalFeedback}</div>
          </motion.div>
        )}
      </div>

      <audio ref={correctSound} src="https://www.soundjay.com/button/sounds/button-4.mp3" />
      <audio ref={wrongSound} src="https://www.soundjay.com/button/sounds/button-10.mp3" />
    </div>
  );
};

export default FractionDecimalFunland;
